import React from "react";
import { Users, Briefcase, FileText, AlertTriangle, Filter, Check } from "lucide-react";
import { TeamWorkloadSummary, TeamMemberWorkload } from "../../services/teamAllocationService";

interface TeamWorkloadRollupProps {
  summary: TeamWorkloadSummary;
  selectedMemberEmail?: string | null;
  onSelectMember: (email: string | null) => void;
  onFilterUnassigned: () => void;
  isUnassignedFiltered: boolean;
}

export const TeamWorkloadRollup: React.FC<TeamWorkloadRollupProps> = ({
  summary,
  selectedMemberEmail,
  onSelectMember,
  onFilterUnassigned,
  isUnassignedFiltered,
}) => {
  const getLoadBadge = (level: TeamMemberWorkload["loadLevel"]) => {
    switch (level) {
      case "light":
        return (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
            Light
          </span>
        );
      case "balanced":
        return (
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
            Balanced
          </span>
        );
      case "heavy":
        return (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            Heavy
          </span>
        );
      case "overloaded":
        return (
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
            Overloaded
          </span>
        );
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <div className="space-y-4">
      {/* KPI Overview Strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>Team Members</span>
            <Users className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <div className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {summary.members.length}{" "}
            <span className="text-xs font-normal text-slate-400">allocated</span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>Proposal Sections</span>
            <Briefcase className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <div className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {summary.totals.sectionsCount - summary.unassigned.sectionsCount}{" "}
            <span className="text-xs font-normal text-slate-400">
              / {summary.totals.sectionsCount} assigned
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>Requirements</span>
            <FileText className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <div className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {summary.totals.requirementsCount - summary.unassigned.requirementsCount}{" "}
            <span className="text-xs font-normal text-slate-400">
              / {summary.totals.requirementsCount} assigned
            </span>
          </div>
        </div>

        <div
          onClick={onFilterUnassigned}
          className={`cursor-pointer rounded-xl border p-3 shadow-2xs transition-colors ${
            isUnassignedFiltered
              ? "border-amber-500 bg-amber-50/60 dark:border-amber-600 dark:bg-amber-950/40"
              : summary.unassigned.totalCount > 0
                ? "border-amber-200 bg-amber-50/30 hover:bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20 dark:hover:bg-amber-950/30"
                : "border-slate-200/80 bg-white dark:border-slate-800 dark:bg-slate-900"
          }`}
        >
          <div className="flex items-center justify-between text-xs text-amber-700 dark:text-amber-400">
            <span>Unassigned Work</span>
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {summary.unassigned.totalCount}{" "}
            <span className="text-xs font-normal text-slate-400">items</span>
          </div>
        </div>
      </div>

      {/* Unassigned Work Alert Banner */}
      {summary.unassigned.totalCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-2.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <span>
              <strong>Action Needed:</strong> {summary.unassigned.totalCount} deliverables remain
              unassigned ({summary.unassigned.sectionsCount} sections,{" "}
              {summary.unassigned.requirementsCount} requirements, {summary.unassigned.formsCount}{" "}
              forms).
            </span>
          </div>
          <button
            type="button"
            onClick={onFilterUnassigned}
            className={`btn-animate inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold ${
              isUnassignedFiltered
                ? "bg-amber-600 text-white hover:bg-amber-700"
                : "bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200"
            }`}
          >
            <Filter className="h-3 w-3" />
            <span>
              {isUnassignedFiltered ? "Showing Unassigned (Clear)" : "View Unassigned Only"}
            </span>
          </button>
        </div>
      )}

      {/* Member Workload Cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {summary.members.map((member) => {
          const isSelected = selectedMemberEmail === member.email;
          const percentComplete =
            member.totalItemsCount > 0
              ? Math.round((member.completedItemsCount / member.totalItemsCount) * 100)
              : 0;

          return (
            <div
              key={member.email}
              onClick={() => onSelectMember(isSelected ? null : member.email)}
              className={`btn-animate cursor-pointer rounded-xl border p-3.5 transition-all ${
                isSelected
                  ? "border-[#2ba5b5] bg-[#2ba5b5]/5 shadow-sm ring-2 ring-[#2ba5b5]/20 dark:bg-[#2ba5b5]/10"
                  : "border-slate-200/80 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
              }`}
            >
              {/* Member Header */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2ba5b5]/15 text-xs font-bold text-[#2ba5b5]">
                    {getInitials(member.displayName)}
                  </div>
                  <div className="min-w-0">
                    <h4 className="truncate text-xs font-semibold text-slate-900 dark:text-white">
                      {member.displayName}
                    </h4>
                    <p className="truncate text-[10px] text-slate-400">
                      {member.department || member.role || member.email}
                    </p>
                  </div>
                </div>
                {getLoadBadge(member.loadLevel)}
              </div>

              {/* Stats Chips */}
              <div className="mt-3 grid grid-cols-2 gap-1.5 text-[11px] text-slate-600 dark:text-slate-300">
                <div className="rounded-md bg-slate-50 p-1.5 dark:bg-slate-800/60">
                  <span className="text-[10px] text-slate-400 block">Sections</span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {member.sectionsOwnedCount}
                  </span>
                  {member.sectionsReviewingCount > 0 && (
                    <span className="text-[10px] text-slate-400 ml-1">
                      (+{member.sectionsReviewingCount} rev)
                    </span>
                  )}
                </div>

                <div className="rounded-md bg-slate-50 p-1.5 dark:bg-slate-800/60">
                  <span className="text-[10px] text-slate-400 block">Reqs / Forms</span>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {member.requirementsCount}
                  </span>
                  <span className="text-[10px] text-slate-400 ml-1">/ {member.formsCount}</span>
                </div>
              </div>

              {/* Words & Progress */}
              <div className="mt-3 space-y-1">
                <div className="flex items-center justify-between text-[10px] text-slate-400">
                  <span>
                    {member.draftedWordCount.toLocaleString()} words
                    {member.targetWordCount > 0 && ` / ${member.targetWordCount.toLocaleString()}`}
                  </span>
                  <span className="font-semibold text-slate-700 dark:text-slate-300">
                    {percentComplete}% done
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <div
                    className="h-full rounded-full bg-[#2ba5b5] transition-all"
                    style={{ width: `${Math.min(percentComplete, 100)}%` }}
                  />
                </div>
              </div>

              {/* Filter active tag */}
              {isSelected && (
                <div className="mt-2.5 flex items-center justify-center gap-1 rounded-md bg-[#2ba5b5] py-0.5 text-[10px] font-semibold text-white">
                  <Check className="h-3 w-3" />
                  <span>Filtered to this Member</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
